import React, { useState, useEffect } from 'react';
import {
    Menu, X, Home, Package, FileText, Calendar, Users, BarChart3, Settings,
    Bell, Search, ChevronDown, ArrowRight, TrendingUp, TrendingDown,
    Building, Hammer, Truck, Star, AlertCircle, CheckCircle, Clock,
    DollarSign, Activity, Globe, Zap, Target, Award, Shield, LogOut,
    ChevronRight, Filter, Download, RefreshCw, MoreVertical, Plus, User, Cable, UserCog, Calculator, Construction, Beaker, Leaf, ShieldCheck, Wrench,
    Edit3, Trash2, Check, X as XIcon, Calendar as CalendarIcon, Flag , Folder , MonitorCog , SquareActivity , Tickets , Receipt , Briefcase
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../Styles/dashboard.css';
import ApiService from '../services/api';
import MepIcon from '../assets/MEP_Solutions.png';
import architecturalIcon from '../assets/architectural_icon.png';
import civilIcon from '../assets/civil-icon.png';
import constructionIcon from '../assets/construction-icon.png';
import powerToolsIcon from '../assets/power-tools_icon.png';
import sustainableIcon from '../assets/sustainable-icon.png';
import hygienicIcon from '../assets/hygienic-icon.png';

const UnitechDashboard = () => {
    const [user, setUser] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeMenuItem, setActiveMenuItem] = useState('dashboard');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [notifications, setNotifications] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [dashboardData, setDashboardData] = useState({
        stats: {},
        activities: [],
        notifications: []
    });

    // To-Do List States
    const [todos, setTodos] = useState([]);
    const [newTodo, setNewTodo] = useState({ title: '', priority: 'medium', dueDate: '' });
    const [editingTodo, setEditingTodo] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [todoFilter, setTodoFilter] = useState('all'); // all, pending, completed
    const [isLoadingTodos, setIsLoadingTodos] = useState(false);

    useEffect(() => {
        checkAuthStatus();
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (user) {
            loadDashboardData();
            loadTodos();
        }
    }, [user]);

    const checkAuthStatus = async () => {
        try {
            const syncUser = ApiService.getUserSync();
            if (syncUser) {
                setUser(syncUser);
                setIsLoading(false);
                return;
            }

            console.log('No user in memory, checking with server...');
            const response = await ApiService.getCurrentUser();
            if (response.success && response.user) {
                setUser(response.user);
            } else {
                console.log('No valid session, redirecting to login...');
                navigate('/login');
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            if (!ApiService.getUserSync()) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const loadDashboardData = async () => {
        if (!user) {
            setIsLoading(true);
        }

        try {
            await new Promise(resolve => setTimeout(resolve, 1000));

            setDashboardData({
                stats: {
                    activeProjects: Math.floor(Math.random() * 50) + 10,
                    monthlyRevenue: (Math.random() * 10 + 5).toFixed(1),
                    deliveryRate: Math.floor(Math.random() * 20) + 80,
                    clientSatisfaction: (Math.random() * 5 + 95).toFixed(1)
                },
                activities: [
                    {
                        id: 1,
                        user: user?.firstName + ' ' + user?.lastName,
                        action: 'Updated inventory levels for concrete materials',
                        time: '2 minutes ago',
                        type: 'inventory'
                    },
                    {
                        id: 2,
                        user: 'Marie Laurent',
                        action: `Assigned ${user?.firstName} to Marina Bay project`,
                        time: '15 minutes ago',
                        type: 'assignment'
                    },
                    {
                        id: 3,
                        user: user?.firstName + ' ' + user?.lastName,
                        action: 'Scheduled team meeting for City Center project',
                        time: '1 hour ago',
                        type: 'planning'
                    },
                    {
                        id: 4,
                        user: user?.firstName + ' ' + user?.lastName,
                        action: 'Approved system configuration changes',
                        time: '2 hours ago',
                        type: 'admin'
                    }
                ],
                notifications: [
                    {
                        id: 1,
                        title: 'New project assigned',
                        message: 'You have been assigned to Marina Bay Development',
                        time: '5 minutes ago',
                        read: false,
                        type: 'assignment'
                    },
                    {
                        id: 2,
                        title: 'Inventory alert',
                        message: 'Steel materials running low in warehouse B',
                        time: '1 hour ago',
                        read: false,
                        type: 'alert'
                    }
                ]
            });

            setNotifications(2);
        } catch (error) {
            console.error('Erreur lors du chargement des données:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // To-Do List Functions
    const loadTodos = async () => {
        setIsLoadingTodos(true);
        try {
            // Simulate API call - replace with actual API call later
            await new Promise(resolve => setTimeout(resolve, 500));

            // Mock data for now
            const mockTodos = [
                {
                    id: 1,
                    title: 'Review Marina Bay project specifications',
                    completed: false,
                    priority: 'high',
                    dueDate: '2025-07-20',
                    createdAt: new Date().toISOString(),
                    userId: user?.id
                },
                {
                    id: 2,
                    title: 'Update inventory management system',
                    completed: true,
                    priority: 'medium',
                    dueDate: '2025-07-18',
                    createdAt: new Date().toISOString(),
                    userId: user?.id
                },
                {
                    id: 3,
                    title: 'Prepare team meeting agenda',
                    completed: false,
                    priority: 'low',
                    dueDate: '2025-07-22',
                    createdAt: new Date().toISOString(),
                    userId: user?.id
                },
                {
                    id: 4,
                    title: 'Check warehouse B steel inventory',
                    completed: false,
                    priority: 'high',
                    dueDate: '2025-07-16',
                    createdAt: new Date().toISOString(),
                    userId: user?.id
                }
            ];

            setTodos(mockTodos);
        } catch (error) {
            console.error('Error loading todos:', error);
        } finally {
            setIsLoadingTodos(false);
        }
    };

    const addTodo = async () => {
        if (!newTodo.title.trim()) return;

        try {
            const todoData = {
                id: Date.now(), // Temporary ID - replace with API response
                title: newTodo.title,
                completed: false,
                priority: newTodo.priority,
                dueDate: newTodo.dueDate,
                createdAt: new Date().toISOString(),
                userId: user?.id
            };

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 300));

            setTodos(prev => [todoData, ...prev]);
            setNewTodo({ title: '', priority: 'medium', dueDate: '' });
            setShowAddForm(false);
        } catch (error) {
            console.error('Error adding todo:', error);
        }
    };

    const toggleTodo = async (id) => {
        try {
            setTodos(prev => prev.map(todo =>
                todo.id === id ? { ...todo, completed: !todo.completed } : todo
            ));

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 200));
        } catch (error) {
            console.error('Error toggling todo:', error);
        }
    };

    const deleteTodo = async (id) => {
        if (!window.confirm('Are you sure you want to delete this task?')) return;

        try {
            setTodos(prev => prev.filter(todo => todo.id !== id));

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 200));
        } catch (error) {
            console.error('Error deleting todo:', error);
        }
    };

    const updateTodo = async (id, updates) => {
        try {
            setTodos(prev => prev.map(todo =>
                todo.id === id ? { ...todo, ...updates } : todo
            ));

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 200));
            setEditingTodo(null);
        } catch (error) {
            console.error('Error updating todo:', error);
        }
    };

    const filteredTodos = todos.filter(todo => {
        switch (todoFilter) {
            case 'pending':
                return !todo.completed;
            case 'completed':
                return todo.completed;
            default:
                return true;
        }
    });

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high':
                return 'rgb(239, 68, 68)'; // red
            case 'medium':
                return 'rgb(245, 158, 11)'; // amber
            case 'low':
                return 'rgb(34, 197, 94)'; // green
            default:
                return 'rgb(156, 163, 175)'; // gray
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const isOverdue = (dueDate) => {
        if (!dueDate) return false;
        return new Date(dueDate) < new Date().setHours(0, 0, 0, 0);
    };

    const handleLogout = async () => {
        if (window.confirm('Are you sure you wanna disconnect?')) {
            ApiService.clearUserData();
            navigate('/login');
        }
    };

    const handleRefresh = () => {
        loadDashboardData();
        loadTodos();
    };

    const menuItems = [
        {
            id: 'barcoding',
            label: 'Barcoding Database',
            icon: FileText,
            path: '/barcoding'
        },
        {
            id: 'mep-solutions',
            label: 'MEP Solutions',
            icon: MepIcon,
            isImage: true,
            path: '/mep_solutions'
        },
        {
            id: 'architectural-solutions',
            label: 'Architectural Solutions',
            icon: architecturalIcon,
            isImage: true,
            path: '/architectural_solutions'
        },
        {
            id: 'civil-solutions',
            label: 'Civil Solutions',
            icon: civilIcon,
            isImage: true,
            path: '/civil_solutions'
        },
        {
            id: 'construction-chemicals',
            label: 'Construction Chemicals',
            icon: constructionIcon,
            isImage: true,
            path: '/construction_chemicals'
        },
        {
            id: 'power-tools',
            label: 'Power & Industrial Tools',
            icon: powerToolsIcon,
            isImage: true,
            path: '/power_tools'
        },
        {
            id: 'sustainable-solutions',
            label: 'Sustainable Solutions',
            icon: sustainableIcon,
            isImage: true,
            path: '/sustainable_solutions'
        },
        {
            id: 'hygienic-solutions',
            label: 'Hygienic Solutions',
            icon: hygienicIcon,
            isImage: true,
            path: '/hygienic_solutions'
        },
        {
            id: 'conversions',
            label: 'Engineering Conversions',
            icon: Calculator,
            path: '/conversion'
        },
        {
            id: 'analytics',
            label: 'Amazon Products',
            icon: BarChart3,
            path: '/amazon_products'
        },
        {
            id: 'team',
            label: 'Team Management',
            icon: Users,
            path: '/team_management'
        },
        {
            id: 'user-management',
            label: 'User Management',
            icon: UserCog,
            path: '/users_management'
        },
    ];

    const filteredMenuItems = menuItems.filter(item => {
        if (item.id === 'team' && user?.role === 'employee') return false;
        return true;
    });

    const quickStats = [
        {
            title: 'Active Projects',
            value: dashboardData.stats.activeProjects || '0',
            change: '+12%',
            trend: 'up',
            icon: Hammer,
            color: 'from-blue-600 to-blue-700'
        },
        {
            title: 'Monthly Revenue',
            value: `€${dashboardData.stats.monthlyRevenue || '0'}M`,
            change: '+8.4%',
            trend: 'up',
            icon: DollarSign,
            color: 'from-emerald-600 to-emerald-700'
        },
        {
            title: 'Delivery Rate',
            value: `${dashboardData.stats.deliveryRate || '0'}%`,
            change: '+5.2%',
            trend: 'up',
            icon: Truck,
            color: 'from-purple-600 to-purple-700'
        },
        {
            title: 'Client Satisfaction',
            value: `${dashboardData.stats.clientSatisfaction || '0'}%`,
            change: '+2.1%',
            trend: 'up',
            icon: Star,
            color: 'from-orange-600 to-orange-700'
        }
    ];

    const getUserInitials = () => {
        if (!user) return 'U';
        const firstInitial = user.firstName ? user.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = user.lastName ? user.lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial || user.email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (!user) return 'Utilisateur';
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.email;
    };

    const getFirstName = () => {
        return user?.firstName || user?.email?.split('@')[0] || 'Utilisateur';
    };

    if (isLoading && !user) {
        return (
            <div className="loading-screen">
                <div className="loading-content">
                    <div className="loading-spinner"></div>
                    <p className="loading-text">UNITECH IKK is Loading</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <>
            <div className="dashboard-container">
                {isLoading && (
                    <div className="loading-overlay">
                        <div className="loading-overlay-content">
                            <div className="loading-overlay-spinner"></div>
                            <span className="loading-overlay-text">Data is Loading</span>
                        </div>
                    </div>
                )}

                {/* Sidebar */}
                <aside className="sidebar">
                    <div className="sidebar-header">
                        <div
                            className="sidebar-logo-container"
                            onClick={() => navigate('/dashboard')}
                            style={{
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderRadius: '8px',
                                padding: '8px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title="Retour au Dashboard"
                        >
                            <div className="sidebar-logo">
                                <Building style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                            <div className="sidebar-brand">
                                <h1 className="sidebar-title">UNITECH IKK</h1>
                                <p className="sidebar-subtitle">Enterprise Dashboard</p>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        {filteredMenuItems.map((item) => (
                            <div
                                key={item.id}
                                className={`nav-item ${activeMenuItem === item.id ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveMenuItem(item.id);
                                    navigate(item.path);
                                }}
                            >
                                {item.isImage ? (
                                    <img
                                        src={item.icon}
                                        alt={item.label}
                                        className="nav-icon-image"
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            display: 'block'
                                        }}
                                    />
                                ) : (
                                    <item.icon
                                        style={{
                                            width: '20px',
                                            height: '20px',
                                            display: 'block'
                                        }}
                                    />
                                )}
                                <span className="nav-text">{item.label}</span>
                            </div>
                        ))}
                    </nav>

                    <div className="sidebar-footer">
                        <div className="user-profile">
                            <div className="user-avatar">{getUserInitials()}</div>
                            <div className="user-info">
                                <p className="user-name">{getDisplayName()}</p>
                                <p className="user-role">{user?.role || 'Utilisateur'}</p>
                            </div>
                            <button
                                className="logout-button"
                                onClick={handleLogout}
                                title="Logout"
                            >
                                <LogOut style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="main-content">
                    {/* Header */}
                    <header className="header">
                        <div className="header-content">
                            <div className="header-left">
                                <button
                                    className="menu-toggle"
                                    onClick={() => setSidebarOpen(!sidebarOpen)}
                                >
                                    {sidebarOpen ?
                                        <X style={{ width: '20px', height: '20px' }} /> :
                                        <Menu style={{ width: '20px', height: '20px' }} />
                                    }
                                </button>

                                <nav className="breadcrumb">
                                    <div className="breadcrumb-item">
                                        <Home style={{ width: '16px', height: '16px' }} />
                                        <span>Dashboard</span>
                                    </div>
                                    <ChevronRight style={{ width: '14px', height: '14px' }} />
                                    <div className="breadcrumb-item">
                                        <span>Overview</span>
                                    </div>
                                </nav>
                            </div>

                            <div className="header-right">
                                <div className="search-container">
                                    <input
                                        type="text"
                                        className="search-input"
                                        placeholder="Rechercher..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    <Search className="search-icon" style={{ width: '18px', height: '18px' }} />
                                </div>

                                <div className="header-actions">
                                    <button
                                        className="header-button"
                                        onClick={handleRefresh}
                                        disabled={isLoading}
                                        title="Actualiser"
                                    >
                                        <RefreshCw style={{
                                            width: '18px',
                                            height: '18px',
                                            transform: isLoading ? 'rotate(360deg)' : 'none',
                                            transition: 'transform 1s linear'
                                        }} />
                                    </button>

                                    <button
                                        className="header-button"
                                        title="Notifications"
                                    >
                                        <Bell style={{ width: '18px', height: '18px' }} />
                                        {notifications.length > 0 && (
                                            <span className="notification-badge">{notifications.length}</span>
                                        )}
                                    </button>

                                    <button
                                        className="header-button"
                                        onClick={() => navigate('/settings')}
                                        title="Paramètres système"
                                    >
                                        <Settings style={{ width: '18px', height: '18px' }} />
                                    </button>

                                    <button
                                        className="header-button"
                                        onClick={() => navigate('/project_management')}
                                        title="Gestion de projets"
                                    >
                                        <Folder style={{ width: '18px', height: '18px' }} />
                                    </button>

                                    <button
                                        className="header-button"
                                        onClick={() => navigate('/technical_support')}
                                        title="Technical Support"
                                    >
                                        <MonitorCog style={{ width: '18px', height: '18px' }} />
                                    </button>

                                    <button
                                        className="header-button"
                                        onClick={() => navigate('/admin_support_it')}
                                        title="Admin Support IT"
                                    >
                                        <SquareActivity style={{ width: '18px', height: '18px' }} />
                                    </button>

                                    <button
                                        className="header-button"
                                        onClick={() => navigate('/mytickets')}
                                        title="My Tickets"
                                    >
                                        <Tickets style={{ width: '18px', height: '18px' }} />
                                    </button>

                                    <button
                                        className="header-button"
                                        onClick={() => navigate('/quotations')}
                                        title="Quotation System"
                                    >
                                        <Receipt style={{ width: '18px', height: '18px' }} />
                                    </button>

                                    <button
                                        className="header-button"
                                        onClick={() => navigate('/clients')}
                                        title="Clients"
                                    >
                                        <Briefcase style={{ width: '18px', height: '18px' }} />
                                    </button>



                                    <div className="time-display">
                                        {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Dashboard Content */}
                    <div className="dashboard-content">
                        {/* Page Header */}
                        <div className="page-header">
                            <h1 className="page-title">Bienvenue, {getFirstName()} 👋</h1>
                            <p className="page-subtitle">
                                Voici un aperçu de vos tâches et activités de construction aujourd'hui.
                            </p>

                            <div className="dashboard-actions">
                                <button
                                    className="action-button"
                                    onClick={() => setShowAddForm(true)}
                                >
                                    <Plus style={{ width: '16px', height: '16px' }} />
                                    Nouvelle Tâche
                                </button>
                                <button className="action-button secondary">
                                    <Download style={{ width: '16px', height: '16px' }} />
                                    Exporter
                                </button>
                                <button className="action-button secondary">
                                    <Filter style={{ width: '16px', height: '16px' }} />
                                    Filters
                                </button>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="stats-grid">
                            {quickStats.map((stat, index) => (
                                <div key={index} className="stat-card">
                                    <div className="stat-header">
                                        <div className={`stat-icon ${
                                            stat.title === 'Active Projects' ? 'blue' :
                                                stat.title === 'Monthly Revenue' ? 'green' :
                                                    stat.title === 'Delivery Rate' ? 'purple' : 'orange'
                                        }`}>
                                            <stat.icon style={{ width: '20px', height: '20px', color: 'white' }} />
                                        </div>
                                        <div className="stat-change">
                                            <TrendingUp style={{ width: '14px', height: '14px' }} />
                                            {stat.change}
                                        </div>
                                    </div>
                                    <div className="stat-value">{stat.value}</div>
                                    <div className="stat-label">{stat.title}</div>
                                </div>
                            ))}
                        </div>

                        {/* Content Grid */}
                        <div className="content-grid">
                            {/* To-Do List */}
                            <div className="content-section">
                                <div className="section-header">
                                    <h2 className="section-title">Ma Liste de Tâches</h2>
                                    <div className="section-actions">
                                        <select
                                            value={todoFilter}
                                            onChange={(e) => setTodoFilter(e.target.value)}
                                            style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                border: '1px solid #e5e7eb',
                                                fontSize: '12px',
                                                marginRight: '8px'
                                            }}
                                        >
                                            <option value="all">Toutes</option>
                                            <option value="pending">En cours</option>
                                            <option value="completed">Terminées</option>
                                        </select>
                                        <button
                                            className="icon-button"
                                            onClick={() => loadTodos()}
                                            disabled={isLoadingTodos}
                                        >
                                            <RefreshCw style={{
                                                width: '16px',
                                                height: '16px',
                                                transform: isLoadingTodos ? 'rotate(360deg)' : 'none',
                                                transition: 'transform 1s linear'
                                            }} />
                                        </button>
                                        <button
                                            className="icon-button"
                                            onClick={() => setShowAddForm(true)}
                                        >
                                            <Plus style={{ width: '16px', height: '16px' }} />
                                        </button>
                                    </div>
                                </div>

                                {/* Add Todo Form */}
                                {showAddForm && (
                                    <div style={{
                                        padding: '16px 24px',
                                        backgroundColor: '#f8fafc',
                                        borderBottom: '1px solid #e5e7eb',
                                        marginBottom: '8px'
                                    }}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <input
                                                type="text"
                                                placeholder="Titre de la tâche..."
                                                value={newTodo.title}
                                                onChange={(e) => setNewTodo(prev => ({ ...prev, title: e.target.value }))}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    fontSize: '14px'
                                                }}
                                                autoFocus
                                            />
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: '12px',
                                            alignItems: 'center',
                                            marginBottom: '12px'
                                        }}>
                                            <select
                                                value={newTodo.priority}
                                                onChange={(e) => setNewTodo(prev => ({ ...prev, priority: e.target.value }))}
                                                style={{
                                                    padding: '6px 8px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                <option value="low">Faible</option>
                                                <option value="medium">Moyenne</option>
                                                <option value="high">Élevée</option>
                                            </select>
                                            <input
                                                type="date"
                                                value={newTodo.dueDate}
                                                onChange={(e) => setNewTodo(prev => ({ ...prev, dueDate: e.target.value }))}
                                                style={{
                                                    padding: '6px 8px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '4px',
                                                    fontSize: '12px'
                                                }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={addTodo}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#3b82f6',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <Check style={{ width: '14px', height: '14px' }} />
                                                Ajouter
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowAddForm(false);
                                                    setNewTodo({ title: '', priority: 'medium', dueDate: '' });
                                                }}
                                                style={{
                                                    padding: '6px 12px',
                                                    backgroundColor: '#6b7280',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <XIcon style={{ width: '14px', height: '14px' }} />
                                                Annuler
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Todo Items */}
                                {isLoadingTodos ? (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        color: '#6b7280'
                                    }}>
                                        <RefreshCw style={{
                                            width: '24px',
                                            height: '24px',
                                            margin: '0 auto 8px',
                                            animation: 'spin 1s linear infinite'
                                        }} />
                                        <p>Chargement des tâches...</p>
                                    </div>
                                ) : filteredTodos.length === 0 ? (
                                    <div style={{
                                        padding: '40px',
                                        textAlign: 'center',
                                        color: '#6b7280'
                                    }}>
                                        <CheckCircle style={{
                                            width: '24px',
                                            height: '24px',
                                            margin: '0 auto 8px'
                                        }} />
                                        <p>
                                            {todoFilter === 'completed' ? 'Aucune tâche terminée' :
                                                todoFilter === 'pending' ? 'Aucune tâche en cours' :
                                                    'Aucune tâche pour le moment'}
                                        </p>
                                    </div>
                                ) : (
                                    filteredTodos.map((todo) => (
                                        <div key={todo.id} className="todo-item" style={{
                                            padding: '16px 24px',
                                            borderBottom: '1px solid rgba(30, 64, 175, 0.05)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            backgroundColor: todo.completed ? '#f8fafc' : 'white',
                                            opacity: todo.completed ? 0.8 : 1
                                        }}>
                                            {/* Checkbox */}
                                            <button
                                                onClick={() => toggleTodo(todo.id)}
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    border: `2px solid ${todo.completed ? '#10b981' : '#d1d5db'}`,
                                                    backgroundColor: todo.completed ? '#10b981' : 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {todo.completed && (
                                                    <Check style={{ width: '12px', height: '12px', color: 'white' }} />
                                                )}
                                            </button>

                                            {/* Content */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                {editingTodo === todo.id ? (
                                                    <div>
                                                        <input
                                                            type="text"
                                                            defaultValue={todo.title}
                                                            onKeyPress={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    updateTodo(todo.id, { title: e.target.value });
                                                                }
                                                            }}
                                                            onBlur={(e) => updateTodo(todo.id, { title: e.target.value })}
                                                            style={{
                                                                width: '100%',
                                                                padding: '4px 8px',
                                                                border: '1px solid #d1d5db',
                                                                borderRadius: '4px',
                                                                fontSize: '14px'
                                                            }}
                                                            autoFocus
                                                        />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <h3 style={{
                                                            margin: 0,
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            color: todo.completed ? '#6b7280' : '#1f2937',
                                                            textDecoration: todo.completed ? 'line-through' : 'none'
                                                        }}>
                                                            {todo.title}
                                                        </h3>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px',
                                                            marginTop: '4px'
                                                        }}>
                                                            {/* Priority */}
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}>
                                                                <Flag style={{
                                                                    width: '12px',
                                                                    height: '12px',
                                                                    color: getPriorityColor(todo.priority)
                                                                }} />
                                                                <span style={{
                                                                    fontSize: '12px',
                                                                    color: '#6b7280',
                                                                    textTransform: 'capitalize'
                                                                }}>
                                                                    {todo.priority === 'high' ? 'Élevée' :
                                                                        todo.priority === 'medium' ? 'Moyenne' : 'Faible'}
                                                                </span>
                                                            </div>

                                                            {/* Due Date */}
                                                            {todo.dueDate && (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px'
                                                                }}>
                                                                    <CalendarIcon style={{
                                                                        width: '12px',
                                                                        height: '12px',
                                                                        color: isOverdue(todo.dueDate) ? '#ef4444' : '#6b7280'
                                                                    }} />
                                                                    <span style={{
                                                                        fontSize: '12px',
                                                                        color: isOverdue(todo.dueDate) ? '#ef4444' : '#6b7280'
                                                                    }}>
                                                                        {formatDate(todo.dueDate)}
                                                                        {isOverdue(todo.dueDate) && !todo.completed && ' (En retard)'}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Actions */}
                                            <div style={{
                                                display: 'flex',
                                                gap: '4px',
                                                flexShrink: 0
                                            }}>
                                                <button
                                                    onClick={() => setEditingTodo(editingTodo === todo.id ? null : todo.id)}
                                                    style={{
                                                        padding: '6px',
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        color: '#6b7280',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                    title="Modifier"
                                                >
                                                    <Edit3 style={{ width: '14px', height: '14px' }} />
                                                </button>
                                                <button
                                                    onClick={() => deleteTodo(todo.id)}
                                                    style={{
                                                        padding: '6px',
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        color: '#ef4444',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                    title="Supprimer"
                                                >
                                                    <Trash2 style={{ width: '14px', height: '14px' }} />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}

                                {/* Todo Stats */}
                                {todos.length > 0 && (
                                    <div style={{
                                        padding: '16px 24px',
                                        borderTop: '1px solid rgba(30, 64, 175, 0.05)',
                                        backgroundColor: '#f8fafc',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '12px',
                                            color: '#6b7280',
                                            display: 'flex',
                                            gap: '16px'
                                        }}>
                                            <span>Total: {todos.length}</span>
                                            <span>En cours: {todos.filter(t => !t.completed).length}</span>
                                            <span>Terminées: {todos.filter(t => t.completed).length}</span>
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                            {Math.round((todos.filter(t => t.completed).length / todos.length) * 100)}% terminé
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Recent Activity */}
                            <div className="content-section">
                                <div className="section-header">
                                    <h2 className="section-title">Activité Récente</h2>
                                    <div className="section-actions">
                                        <button className="icon-button">
                                            <RefreshCw style={{ width: '16px', height: '16px' }} />
                                        </button>
                                    </div>
                                </div>

                                {dashboardData.activities.map((activity) => (
                                    <div key={activity.id} className="activity-item">
                                        <div className="activity-header">
                                            <span className="activity-user">{activity.user}</span>
                                            <span className="activity-time">{activity.time}</span>
                                        </div>
                                        <p className="activity-action">{activity.action}</p>
                                        <div className="activity-type">
                                            <span className={`type-badge type-${activity.type}`}>
                                                {activity.type}
                                            </span>
                                        </div>
                                    </div>
                                ))}

                                <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(30, 64, 175, 0.05)' }}>
                                    <button className="action-button secondary" style={{ width: '100%', justifyContent: 'center' }}>
                                        Voir toutes les activités
                                        <ArrowRight style={{ width: '16px', height: '16px' }} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Status Footer */}
                        <div className="status-footer">
                            <div className="status-indicator">
                                <div className="status-operational">
                                    <div className="status-dot"></div>
                                    <Activity style={{ width: '16px', height: '16px' }} />
                                    All Systems are Running
                                </div>
                            </div>

                            <div className="status-info">
                                <span>Dernière mise à jour: {currentTime.toLocaleTimeString('fr-FR')}</span>
                                <span>•</span>
                                <span>Version 1.0</span>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default UnitechDashboard;